import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import NotFound from "@/pages/NotFound";
import Auth from "@/pages/Auth";
import Index from "@/pages/Index";
import ServicePage from "@/pages/ServicePage";
import CreateService from "@/pages/CreateService";
import ServiceCreated from "@/pages/ServiceCreated";
import Dashboard from "@/pages/Dashboard";
import ProfilePage from "@/pages/ProfilePage";
import Onboarding from "@/pages/Onboarding";
import SimpleOnboarding from "@/pages/SimpleOnboarding";
import PricingPage from "@/pages/Pricing";
import ServiceShare from "@/pages/ServiceShare";
import PaymentMethods from "@/pages/PaymentMethods";
import SocialLinks from "@/pages/SocialLinks";
import StoreOptimization from "@/pages/StoreOptimization";
import { AuthCallback } from "@/components/AuthCallback";
import AddServices from "@/pages/AddServices";
import TelegramConnect from "@/pages/TelegramConnect";
import CustomerServiceRequest from "@/pages/CustomerServiceRequest";
import { ActivityTracker } from "@/components/ActivityTracker";
import JChatBot from "@/components/chat/JChatBot";
import ProtectedRoute from "@/components/ProtectedRoute";
import { useLanguageDetection } from "@/hooks/useLanguageDetection";
import "./index.css";
import "./App.css";

// Get base path from import.meta.env for GitHub Pages
const basePath = import.meta.env.BASE_URL || '/';

function App() {
  // Initialize language detection
  const { isReady } = useLanguageDetection();

  // Show loading state while language is being detected
  if (!isReady) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <img 
          src="/lovable-uploads/f8714beb-a432-4da6-acab-3ef6f0a42367.png" 
          alt="Loading" 
          className="h-10 w-10 gebeya-spinner" 
        />
      </div>
    );
  }

  return (
      <div className="bg-white min-h-screen">
        <Router basename={basePath}>
          <ActivityTracker />
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
            <Route path="/simple-onboarding" element={<ProtectedRoute><SimpleOnboarding /></ProtectedRoute>} />
            <Route path="/telegram-connect" element={<ProtectedRoute><TelegramConnect /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/service-page" element={<ServicePage />} />
            <Route path="/services/:username" element={<ServicePage />} />
            <Route path="/s/:username" element={<ServicePage />} />
            <Route path="/create-service" element={<ProtectedRoute><CreateService /></ProtectedRoute>} />
            <Route path="/service-created" element={<ProtectedRoute><ServiceCreated /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/dashboard/service-requests" element={<ProtectedRoute><Dashboard initialView="service-requests" /></ProtectedRoute>} />
            <Route path="/dashboard/service-requests/:requestId" element={<ProtectedRoute><Dashboard initialView="service-requests" viewMode="detail" /></ProtectedRoute>} />
            <Route path="/dashboard/services" element={<ProtectedRoute><Dashboard initialView="services" /></ProtectedRoute>} />
            <Route path="/dashboard/categories" element={<ProtectedRoute><Dashboard initialView="categories" /></ProtectedRoute>} />
            <Route path="/dashboard/customers" element={<ProtectedRoute><Dashboard initialView="customers" /></ProtectedRoute>} />
            <Route path="/dashboard/marketing" element={<ProtectedRoute><Dashboard initialView="marketing" /></ProtectedRoute>} />
            <Route path="/dashboard/availability" element={<ProtectedRoute><Dashboard initialView="availability" /></ProtectedRoute>} />
            <Route path="/dashboard/settings" element={<ProtectedRoute><Dashboard initialView="settings" /></ProtectedRoute>} />
            <Route path="/dashboard/settings/:section" element={<ProtectedRoute><Dashboard initialView="settings" /></ProtectedRoute>} />
            <Route path="/pricing" element={<ProtectedRoute><PricingPage /></ProtectedRoute>} />
            <Route path="/service-share" element={<ServiceShare />} />
            <Route path="/payment-methods" element={<ProtectedRoute><PaymentMethods /></ProtectedRoute>} />
            <Route path="/social-links" element={<SocialLinks />} />
            <Route path="/store-optimization" element={<ProtectedRoute><StoreOptimization /></ProtectedRoute>} />
            <Route path="/add-services" element={<ProtectedRoute><AddServices /></ProtectedRoute>} />
            <Route path="/service-request/:requestId/:requestToken" element={<CustomerServiceRequest />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <Toaster />
          <JChatBot />
        </Router>
      </div>
  );
}

export default App;